import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adicionarMeses,
  mesDe,
  zReajuste,
  type PlanoNaLista,
  type SimulacaoDeReajuste,
} from '@arena/shared/schemas';
import { dataValida } from '@arena/shared/tempo';
import { dataCurta, dinheiro } from '../agenda/formatos.js';
import { useAtrasado } from '../agenda/useAtrasado.js';
import { errosPorCampo, primeiraMensagem } from '../alunos/formatos.js';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { planosApi } from '../api/planos.js';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { dinheiroComCentavos, percentualParaCentesimos } from './formatos.js';

/**
 * `#m-reajuste` — percentual, plano e vigência futura, com a simulação
 * antes de aplicar: "De R$ 160,00 para R$ 169,60. Impacto estimado de
 * +R$ 1.786 por mês. As faturas já geradas não mudam."
 *
 * A simulação vem do SERVIDOR, e não de uma conta na tela: ela depende de
 * cada contrato do plano — o valor em vigor na véspera da vigência, o
 * desconto de cada um, quem começa depois. Aplicar refaz a mesma conta
 * dentro da transação que grava; é o que faz a simulação bater com o
 * impacto real.
 */
export function ModalDeReajuste({
  planos,
  hoje,
  onFechar,
}: {
  planos: readonly PlanoNaLista[];
  hoje: string;
  onFechar: () => void;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();

  const [planoId, setPlanoId] = useState<string>(planos[0]?.id ?? '');
  const [percentual, setPercentual] = useState('6%');
  // O protótipo abre em 02/09 com a vigência em 01/11: o primeiro dia do
  // mês seguinte ao próximo — tempo de avisar os alunos.
  const [vigencia, setVigencia] = useState(`${adicionarMeses(mesDe(hoje), 2)}-01`);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const centesimos = percentualParaCentesimos(percentual);
  const pedido = {
    planoId: planoId === 'todos' ? null : planoId,
    percentual: centesimos ?? 0,
    vigencia,
  };
  const valido = zReajuste.safeParse(pedido).success && vigencia > hoje;
  const pedidoAtrasado = useAtrasado(pedido, 350);

  const simulacao = useQuery({
    queryKey: ['planos', 'reajuste', 'simulacao', pedidoAtrasado],
    queryFn: () => planosApi.simularReajuste(zReajuste.parse(pedidoAtrasado)),
    enabled: valido && zReajuste.safeParse(pedidoAtrasado).success,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const aplicar = useMutation({
    mutationFn: () => planosApi.aplicarReajuste(zReajuste.parse(pedido)),
    onSuccess: () =>
      Promise.all([
        cliente.invalidateQueries({ queryKey: ['planos'] }),
        cliente.invalidateQueries({ queryKey: ['cobrancas'] }),
      ]),
  });

  async function confirmar() {
    setErros({});
    setErroGeral(null);
    if (centesimos === null) {
      setErros({ percentual: 'Percentual inválido — use 6% ou 4,62.' });
      return;
    }
    const validacao = zReajuste.safeParse(pedido);
    if (!validacao.success) {
      setErros(errosPorCampo(validacao.error.issues));
      return;
    }
    if (!dataValida(vigencia) || vigencia <= hoje) {
      setErros({ vigencia: 'A vigência precisa ser uma data futura.' });
      return;
    }
    try {
      const aplicado = await aplicar.mutateAsync();
      avisar(
        `Reajuste agendado para ${dataCurta(aplicado.vigencia)}: ${aplicado.assinaturas} ` +
          `${aplicado.assinaturas === 1 ? 'assinatura' : 'assinaturas'}.`,
      );
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Reajuste em lote"
      descricao="Aplica a todos os alunos do plano escolhido."
      largura="estreita"
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void confirmar()} disabled={aplicar.isPending || !valido}>
            {aplicar.isPending ? 'Aplicando…' : 'Aplicar reajuste'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <Campo rotulo="Plano" erro={erros['planoId']}>
          <select value={planoId} onChange={(e) => setPlanoId(e.target.value)}>
            {planos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
                {p.alunos !== undefined
                  ? ` — ${p.alunos} ${p.alunos === 1 ? 'aluno' : 'alunos'}`
                  : ''}
              </option>
            ))}
            <option value="todos">Todos os planos</option>
          </select>
        </Campo>
        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Percentual" erro={erros['percentual']}>
            <input
              inputMode="decimal"
              value={percentual}
              onChange={(e) => setPercentual(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Vigência a partir de" erro={erros['vigencia']}>
            <input type="date" value={vigencia} onChange={(e) => setVigencia(e.target.value)} />
          </Campo>
        </Formulario>

        {valido && simulacao.data && <NotaDaSimulacao simulacao={simulacao.data} />}
        {valido && simulacao.isError && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{mensagemDoErro(simulacao.error)}</div>
          </div>
        )}
        {!valido && (
          <Nota>Escolha um percentual diferente de zero e uma vigência futura para simular.</Nota>
        )}

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}

function NotaDaSimulacao({ simulacao }: { simulacao: SimulacaoDeReajuste }) {
  const [unico] = simulacao.planos;
  const sinal = simulacao.impacto >= 0 ? '+' : '−';
  return (
    <Nota>
      {simulacao.planos.length === 1 && unico ? (
        <>
          De {dinheiroComCentavos(unico.valorAtual)} para{' '}
          <b>{dinheiroComCentavos(unico.valorNovo)}</b>.{' '}
        </>
      ) : (
        <>
          {simulacao.planos.length} planos · {simulacao.assinaturas}{' '}
          {simulacao.assinaturas === 1 ? 'assinatura' : 'assinaturas'}.{' '}
        </>
      )}
      Impacto estimado de{' '}
      <b>
        {sinal}
        {dinheiro(Math.abs(simulacao.impacto))} por mês
      </b>{' '}
      na receita recorrente. As faturas já geradas não mudam
      {simulacao.faturasJaGeradas > 0
        ? ` — ${simulacao.faturasJaGeradas} já nasceram com vencimento depois da vigência e ficam com o valor de hoje`
        : ''}
      .
      {simulacao.ignoradas.length > 0 &&
        ` ${simulacao.ignoradas.length} ${simulacao.ignoradas.length === 1 ? 'contrato fica' : 'contratos ficam'} de fora: ${simulacao.ignoradas[0]?.motivo}`}
    </Nota>
  );
}
